// About surface: a header info icon that opens a modal dialog. Mirrors
// ServiceAI's "About ServiceAI" TelerikWindow (ServiceAI.Client/Layout/
// MainLayout.razor) — logo, version line, a "Check for updates" force-refresh
// action, the Support / Terms / Privacy link row, and a copyright line.
//
// Self-contained like OrionSupport/GettingStarted: owns its own open state and
// renders its dialog, so AppShell just drops <About /> into the header cluster.
//
// Version + build time come from the Vite `define` constants baked at build
// time (vite.config.ts). ServiceAI fetches a CI-written version.json at runtime;
// the bundle constant is the React-idiomatic equivalent — it always reflects the
// exact build that's running.

import { useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { SvgIcon } from '@progress/kendo-react-common';
import { questionCircleIcon } from '@progress/kendo-svg-icons';
import { formatDateTime } from '@shared/datetime';

// External destinations match ServiceAI's About links (CloudRadial properties).
const SUPPORT_URL = 'https://support.cloudradial.com/hc';
const TERMS_URL = 'https://www.cloudradial.com/terms';
const PRIVACY_URL = 'https://www.cloudradial.com/privacy';

const linkStyle: React.CSSProperties = { color: 'var(--accent)', textDecoration: 'none' };

// Best-effort, human-readable build timestamp; falls back to the raw ISO string
// if the injected value can't be parsed.
function formatBuildTime(iso: string): string {
  return formatDateTime(iso, iso);
}

export function About() {
  const [open, setOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // updateServiceWorker(true) activates a waiting build and reloads onto it.
  // Calling useRegisterSW here in addition to UpdatePrompt is safe — the plugin
  // registration is module-level/idempotent.
  const { updateServiceWorker } = useRegisterSW();

  // "Check for updates" — the ServiceAI CheckForUpdates() equivalent. Evict every
  // Cache Storage entry (so the precache can't keep serving stale assets), then
  // hand off to the service worker to activate the newest build and reload. If no
  // worker is waiting, updateServiceWorker resolves without navigating and the
  // explicit reload below performs a plain hard refresh.
  const forceRefresh = async () => {
    setRefreshing(true);
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch {
      // Cache eviction is best-effort; never block the reload on it.
    }
    try {
      await updateServiceWorker(true);
    } catch {
      // No SW / not supported — fall through to the hard reload.
    }
    window.location.reload();
  };

  return (
    <>
      <button
        type="button"
        className="header-icon-btn"
        aria-label="About"
        title="About CloudRadial AutomationAI"
        onClick={() => setOpen(true)}
      >
        <SvgIcon icon={questionCircleIcon} />
      </button>

      {open && (
        <Dialog title="About" onClose={() => setOpen(false)} width={400}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: 10,
              padding: '8px 4px 4px',
            }}
          >
            {/* Logo — the AutomationAI brand mark. The dialog renders in a portal
                outside .app-shell, so .about-logo carries its own global
                dark-theme swap rule (AppShell.css) rather than the scoped
                .brand-mark one. */}
            <img
              className="about-logo"
              src="/icons/light/automationai-128.png"
              alt=""
              aria-hidden="true"
              width={56}
              height={56}
              style={{ objectFit: 'contain' }}
            />

            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--fg)' }}>
              CloudRadial AutomationAI
            </div>

            <div style={{ fontSize: 13, color: 'var(--fg-muted)' }}>
              Version {__APP_VERSION__}
              <div style={{ fontSize: 11, marginTop: 2 }}>Built {formatBuildTime(__BUILD_TIME__)}</div>
            </div>

            <Button
              fillMode="flat"
              themeColor="primary"
              disabled={refreshing}
              onClick={() => void forceRefresh()}
            >
              {refreshing ? 'Refreshing…' : 'Check for updates'}
            </Button>

            <div style={{ fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.6 }}>
              <div>
                <a href={SUPPORT_URL} target="_blank" rel="noreferrer" style={linkStyle}>
                  Support
                </a>{' '}
                |{' '}
                <a href={TERMS_URL} target="_blank" rel="noreferrer" style={linkStyle}>
                  Terms of Service
                </a>{' '}
                |{' '}
                <a href={PRIVACY_URL} target="_blank" rel="noreferrer" style={linkStyle}>
                  Privacy Policy
                </a>
              </div>
              <div>© {new Date().getFullYear()} CloudRadial. All rights reserved.</div>
            </div>
          </div>

          <DialogActionsBar>
            <Button onClick={() => setOpen(false)}>Close</Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </>
  );
}

export default About;
